import React, { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/reduxHooks'
import { State } from '../../../../redux/store.types'
import {
  minimizeAll,
  minimizeWindow,
  moveWindowToTop,
  unminimizeWindow
} from '../../../../redux/slices/WindowSlice/WindowSlice'
import { openedWindow } from '../../../../redux/slices/WindowSlice/WindowSlice.types'
import {
  Badge,
  Box,
  ClickAwayListener,
  IconButton,
  Typography
} from '@mui/material'
import MenuDropdown from './components/MenuDropdown/MenuDropdown'
import { useMuiTheme } from '../../../../config/muiProvider/MuiProvider'
import ProfileCircleIcon from '../../../../assets/icons/profile_circle.svg'
import LightModeIcon from '../../../../assets/icons/light_mode.svg'
import NightModeIcon from '../../../../assets/icons/night_mode.svg'
import { selectUser } from '../../../../redux/slices/UserSlice/UserSlice.selectors'
import moment from 'moment'
import NotificationDropdown from './components/notificationDropdown/NotificationDropdown'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import useUserNotifications from '../../hooks/useUserNotifications'
import BlurListener from 'src/components/blurListener/blurListener'

const DesktopBar = () => {
  const { openedWindows, windowsOrder } = useAppSelector(
    (state: State) => state.window
  )

  // redux hooks
  const dispatch = useAppDispatch()
  const { userName } = useAppSelector(selectUser)

  const { toggleColorMode, colorMode } = useMuiTheme()

  const notificationsData = useUserNotifications()

  const [openNotificationDropdown, setOpenNotificationDropdown] =
    useState(false)

  const isWindowActive = (windowId: string) => {
    if (!(windowsOrder[windowsOrder.length - 1] === windowId)) return false
    if (openedWindows.find((window) => window.id === windowId)?.minimized) {
      return false
    }
    return true
  }

  const lastActiveWindowId = windowsOrder
    .slice()
    .reverse()
    .find(
      (windowId) =>
        openedWindows.find((window) => window.id === windowId)!.minimized ===
        false
    )

  const handleClickWindow = (window: openedWindow) => {
    if (!window.minimized && lastActiveWindowId === window.id) {
      return dispatch(
        minimizeWindow({
          id: window.id
        })
      )
    }
    if (window.minimized) {
      dispatch(
        unminimizeWindow({
          id: window.id
        })
      )
    }
    dispatch(
      moveWindowToTop({
        id: window.id
      })
    )
  }

  const handleMinimizeAll = (event: React.MouseEvent) => {
    event.preventDefault()
    dispatch(minimizeAll())
  }

  const handleToggleColorMode = () => {
    toggleColorMode()
  }

  const handleToggleNotificationDropdown = () => {
    setOpenNotificationDropdown((oldVal) => !oldVal)
  }

  const handleClickOutsideBar = () => {
    setOpenNotificationDropdown(false)
  }

  return (
    <BlurListener onBlur={handleClickOutsideBar}>
      <ClickAwayListener
        onClickAway={handleClickOutsideBar}
        mouseEvent={'onMouseDown'}
        disableReactTree
      >
        <Box
          sx={{
            width: '100vw',
            padding: '6px',
            position: 'relative',
            zIndex: 99999999,
            display: 'flex',
            '& .MuiTypography-root': {
              fontSize: '12px'
            }
          }}
        >
          <Box
            sx={{
              width: '98%',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              borderTopLeftRadius: '10px',
              borderBottomLeftRadius: '10px',
              background: 'rgba( 255, 255, 255, 0.8 )',
              border: '1px solid rgba( 255, 255, 255, 0.18 )',
              padding: '.25rem'
            }}
          >
            <Box>
              <MenuDropdown />
            </Box>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                gap: '.25rem',
                marginLeft: '1rem',
                '& > *:not(:first-of-type)': {
                  marginLeft: '1rem'
                }
              }}
            >
              {openedWindows.map((window) => {
                return (
                  <Box
                    key={window.id}
                    sx={{
                      height: '100%',
                      width: '1.8rem',
                      opacity: `${isWindowActive(window.id) ? '1' : '.5'}`,
                      cursor: 'pointer',
                      borderBottom:
                        window.id === lastActiveWindowId
                          ? '2px solid white'
                          : undefined,
                      padding: '0.25rem 0'
                    }}
                    onClick={() => handleClickWindow(window)}
                  >
                    <img
                      src={window.icon}
                      alt={window.name}
                      style={{
                        width: '100%',
                        height: '100%'
                      }}
                    />
                  </Box>
                )
              })}
            </Box>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                marginLeft: 'auto',
                '& > *:not(:last-child)': {
                  marginRight: '.8rem'
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <img
                  src={ProfileCircleIcon}
                  height='20px'
                  width='20px'
                  style={{
                    marginRight: '.25rem'
                  }}
                />
                <Typography
                  sx={{
                    color: 'black'
                  }}
                >
                  {userName}
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    color: 'black'
                  }}
                >
                  ESP
                </Typography>
              </Box>
              <IconButton
                onClick={handleToggleColorMode}
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {colorMode === 'dark' ? (
                  <img src={NightModeIcon} width='16px' height='16px' />
                ) : (
                  <img src={LightModeIcon} width='16px' height='16px' />
                )}
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: ' center'
                }}
              >
                <Typography
                  sx={{
                    color: 'black'
                  }}
                >
                  {moment().format('hh:mm A')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: 'black'
                  }}
                >
                  {moment().format('DD-MM-YYYY')}
                </Typography>
              </Box>
              <IconButton
                sx={{
                  color: 'black'
                }}
                onClick={handleToggleNotificationDropdown}
                data-testid='notification-dropdown-button'
              >
                <Badge
                  badgeContent={notificationsData.notifications.length}
                  color='primary'
                >
                  <MailOutlineIcon />
                </Badge>
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              height: '100%',
              width: '2%',
              borderTopRightRadius: '5px',
              borderBottomRightRadius: '5px',
              transition: 'all 0.3s ease-in-out',
              background: 'rgba( 255, 255, 255, 0.8 )',
              border: '1px solid rgba( 255, 255, 255, 0.18 )',
              borderLeft: '1.5px solid rgba( 230, 230, 230, 0.8 )',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'white'
              }
            }}
            onClick={handleMinimizeAll}
          />
          <NotificationDropdown
            {...notificationsData}
            open={openNotificationDropdown}
            onClose={() => {
              setOpenNotificationDropdown(false)
            }}
          />
        </Box>
      </ClickAwayListener>
    </BlurListener>
  )
}

export default DesktopBar
